'use client';

import { PinInput as MantinePinInput } from '@mantine/core';
import type { PinInputProps as MantinePinInputProps } from '@mantine/core';
import { useTranslation } from 'next-i18next/pages';
import { Stack } from '../layout';
import { Text } from '../text';

import type { LockedProps } from '../types';

export type PinInputProps = Omit<
  MantinePinInputProps,
  LockedProps | 'error' | 'manageFocus' | 'placeholder' | 'radius' | 'size' | 'variant'
> & {
  error?: string;
};

/**
 * ### PinInput
 *
 * @description
 * A pin input component that allows the user to input a verification code.
 *
 * @example
 * <PinInput value={value} onChange={onChange} error={error} />
 *
 * @param props - {@link PinInputProps}
 * @returns React component
 */
export const PinInput = ({
  error,
  length = 6,
  type = 'number',
  inputMode = 'numeric',
  inputType = 'number',
  ...rest
}: PinInputProps) => {
  const { t } = useTranslation();
  return (
    <Stack gap="xs">
      <MantinePinInput
        {...rest}
        aria-label={t('Verification code')}
        error={!!error}
        inputMode={inputMode}
        inputType={inputType}
        length={length}
        manageFocus
        placeholder=""
        // PinInput resolves the shared `Input` theme key, so it would otherwise inherit the 4px
        // corner meant for 36px form fields. These are 60px boxes; keep the roomier radius.
        radius="md"
        size="xl"
        type={type}
      />
      {/* Mantine PinInput doesn't support error messages - adding here for consistency with other inputs */}
      {error && (
        <Text size="sm" c="var(--mantine-color-error)" role="alert" aria-live="polite" aria-atomic>
          {error}
        </Text>
      )}
    </Stack>
  );
};
